// slices/usersSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/users`;

// Get auth token for API calls
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Fetch all users
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (filters = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${BASE_URL}/admin/users/?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch users');
    }
  }
);

// Create user
export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/users/`,
        userData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to create user');
    }
  }
);

// Update user
export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, ...userData }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${BASE_URL}/admin/users/${id}/`,
        userData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update user');
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId, thunkAPI) => {
    try {
      await axios.delete(
        `${BASE_URL}/admin/users/${userId}/`,
        { headers: getAuthHeaders() }
      );
      return userId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to delete user');
    }
  }
);

// Bulk create student users
export const bulkCreateStudentUsers = createAsyncThunk(
  'users/bulkCreateStudentUsers',
  async (bulkData, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/users/bulk-create-students/`,
        bulkData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to bulk create users');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    loading: false,
    error: null,
    lastCreatedUser: null, // Store generated credentials
    bulkCreateResult: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLastCreatedUser: (state) => {
      state.lastCreatedUser = null;
    },
    clearBulkCreateResult: (state) => {
      state.bulkCreateResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload);
        state.lastCreatedUser = action.payload; // Store for displaying credentials
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(u => u.id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk create
      .addCase(bulkCreateStudentUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCreateStudentUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkCreateResult = action.payload;
        // Refresh users list
      })
      .addCase(bulkCreateStudentUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearLastCreatedUser, clearBulkCreateResult } = usersSlice.actions;
export default usersSlice.reducer;
